/* <card-wishlist>: the favourites heart on product cards, drawn by the theme,
   backed by Klaviyo's Customer Hub favourites.

   WHY. card-product.liquid used to ship an empty .klaviyo-favorites-plp-slot
   on every card and Klaviyo's embed mounted a widget into each one after its
   script loaded. On a collection page that is well over a hundred slots, and
   Klaviyo's pass over them (one function, rescanning on every DOM change,
   forcing a style recalculation per slot) was the longest freeze in the
   page's performance traces (2026-10-21), with the hearts popping in last.

   WHAT. Two changes, the product page's wishlist-slot.js pattern applied to
   cards:
   1. The heart is a theme-rendered placeholder that paints with the page and
      stays the visual for good. Klaviyo's control is injected invisible and
      only ever supplies the saved state (mirrored from its
      .kl-hub-wishlist-icon--active class into `is-saved`) and the click
      target (the placeholder forwards clicks to it, or queues one until it
      has painted).
   2. Klaviyo's slot is only added once the card comes within reach of the
      viewport, and refresh() is asked for once per batch of new slots rather
      than per slot. Klaviyo then has a dozen slots to deal with at a time,
      not the whole page.

   Cards cloned into the page hidden (the rails' mount-ahead templates) get
   their slot when they are first shown, since a display:none element never
   intersects. A click before the slot exists adds it at once.

   On a product page none of this starts until the buy button is ready
   (window.overkillWishlistReady, 2026-11-03), so the rails' hearts don't put
   Klaviyo to work while PreProduct's button is still loading. */
if (!customElements.get('card-wishlist')) {
  const SLOT_CLASS = 'klaviyo-favorites-plp-slot';
  const ACTIVE_CLASS = 'kl-hub-wishlist-icon--active';

  // One refresh per burst of new slots. Klaviyo's own first pass covers any
  // slot present when its script arrives, so the call is only needed for
  // slots added after that; calling it when klaviyoFavorites is not there
  // yet would be a no-op anyway.
  let refreshTimer = null;
  const requestRefresh = () => {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      const api = window.klaviyoFavorites;
      if (api && typeof api.refresh === 'function') {
        try {
          api.refresh();
        } catch (error) {
          /* Klaviyo's problem, not the card's */
        }
      }
    }, 200);
  };

  const viewport =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) entry.target.ensureSlot();
            });
          },
          { rootMargin: '300px 0px' }
        )
      : null;

  customElements.define(
    'card-wishlist',
    class CardWishlist extends HTMLElement {
      connectedCallback() {
        this.placeholder = this.querySelector('[data-wishlist-placeholder]');
        if (!this.placeholder) return;
        this.queued = false;
        this.placeholder.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.onClick();
        });
        // On a product page, hold off until the buy button is ready
        // (window.overkillWishlistReady, layout/theme.liquid); elsewhere the
        // promise is already resolved. A tap still adds the slot at once.
        (window.overkillWishlistReady || Promise.resolve()).then(() => {
          if (!this.isConnected || this.slot_) return;
          if (viewport) viewport.observe(this);
          else this.ensureSlot();
        });
      }

      disconnectedCallback() {
        if (viewport) viewport.unobserve(this);
        this.stopWatching();
      }

      get slot_() {
        return this.querySelector(`.${SLOT_CLASS}`);
      }

      // Klaviyo's control is not guaranteed to be a <button>: prefer its
      // custom element, then any interactive child, then the first child.
      get widget() {
        const slot = this.slot_;
        if (!slot) return null;
        return (
          slot.querySelector('klaviyo-favorites-button') ||
          slot.querySelector('button, a, [role="button"]') ||
          slot.firstElementChild
        );
      }

      // Adds Klaviyo's anchor once, with the data card-product.liquid carried
      // on the old slot, and asks for a refresh.
      ensureSlot() {
        if (this.slot_) return;
        if (viewport) viewport.unobserve(this);
        const slot = document.createElement('div');
        slot.className = SLOT_CLASS;
        slot.dataset.productId = this.dataset.productId || '';
        slot.dataset.productUrl = this.dataset.productUrl || '';
        slot.dataset.variantId = this.dataset.variantId || '';
        this.appendChild(slot);
        this.watch(slot);
        requestRefresh();
      }

      // Saved state and readiness come from the slot's subtree. Klaviyo
      // renders with shadow DOM for the control itself but swaps the heart
      // classes in the light DOM, so a subtree observer sees both the
      // injection and later toggles.
      watch(slot) {
        this.stopWatching();
        this.observer = new MutationObserver(() => this.sync());
        this.observer.observe(slot, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
        this.sync();
      }

      stopWatching() {
        if (this.observer) this.observer.disconnect();
        this.observer = null;
      }

      painted() {
        const widget = this.widget;
        if (!widget) return false;
        if (widget.shadowRoot && widget.shadowRoot.childElementCount > 0) return true;
        return widget.childElementCount > 0 || widget.offsetHeight > 8;
      }

      sync() {
        const slot = this.slot_;
        const saved = !!(slot && slot.querySelector(`.${ACTIVE_CLASS}`));
        if (saved !== this.classList.contains('is-saved')) {
          this.classList.toggle('is-saved', saved);
          this.placeholder.setAttribute('aria-pressed', String(saved));
        }
        if (!this.classList.contains('wish-ready') && this.painted()) {
          this.classList.add('wish-ready');
          if (this.queued) {
            this.queued = false;
            this.forward();
          }
        }
      }

      forward() {
        const widget = this.widget;
        if (widget && typeof widget.click === 'function') widget.click();
      }

      onClick() {
        if (this.classList.contains('wish-ready')) {
          this.forward();
          return;
        }
        // Not ready: make sure Klaviyo has something to fill, and replay the
        // click once it has.
        this.queued = true;
        this.ensureSlot();
      }
    }
  );
}
